import { useQuery } from '@tanstack/react-query';
import { LayoutTemplate } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { panel } from '@/components/surface/tones';
import { IconTile, Pill, Skeleton } from '@/components/surface/ui';
import { AUTOMATION_CATALOG_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/modules/monitoring/format';
import { serverQuery } from '@/modules/automation/parameters';
import { PageHeader, RunStatusPill } from '@/modules/automation/ui';
import type { TemplateSummary } from '@/types/api';

/**
 * Templates: a tested sequence of playbooks that prepares a server for one purpose.
 *
 * Each is versioned. A run records the version and every step with the values it ran with, so a run
 * read months later says what actually happened rather than what the template says today.
 */
export function TemplatesPage() {
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 60_000 });
  const [searchParams] = useSearchParams();
  const carry = serverQuery(searchParams);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        description="Prepare a server for a purpose in one run: each template is a versioned sequence of playbooks, checked on every server first and runnable as a dry run."
      />

      {catalog.isLoading && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 min-[1800px]:grid-cols-3">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-56" />
          ))}
        </div>
      )}

      {catalog.isError && <p className="text-sm text-danger">The templates could not be loaded: {getErrorMessage(catalog.error)}</p>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 min-[1800px]:grid-cols-3">
        {(catalog.data?.templates ?? []).map((template) => (
          <TemplateCard key={template.key} template={template} carry={carry} />
        ))}
      </div>
    </div>
  );
}

function TemplateCard({ template, carry }: { template: TemplateSummary; carry: string }) {
  return (
    <Link
      to={`/automation/templates/${template.key}${carry}`}
      className={cn(panel, 'group flex flex-col gap-4 p-5 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
    >
      <div className="flex items-start gap-3">
        <IconTile icon={LayoutTemplate} tone="primary" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-semibold leading-6 tracking-tight group-hover:text-primary">{template.name}</h2>
            <Pill tone="muted">v{template.version}</Pill>
          </div>
          <p className="text-[13px] text-muted-foreground">{template.summary}</p>
        </div>
      </div>

      <ol className="flex flex-wrap items-center gap-1.5 text-xs">
        {template.steps.map((step, index) => (
          <li key={`${step.playbook}-${index}`} className="flex items-center gap-1.5">
            {index > 0 && <span aria-hidden className="text-muted-foreground">→</span>}
            <span className="rounded-md bg-slate-900/[0.05] px-2 py-0.5 dark:bg-white/[0.06]">
              {step.label}
              {step.optional && <span className="text-muted-foreground"> (optional)</span>}
            </span>
          </li>
        ))}
      </ol>

      <p className="text-xs text-muted-foreground">{template.supported_systems}</p>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-slate-900/[0.07] pt-3 text-xs dark:border-white/[0.07]">
        {template.last_run !== null ? (
          <span className="flex items-center gap-2">
            <RunStatusPill status={template.last_run.status} label={template.last_run.status_label} />
            <span className="text-muted-foreground">{relativeTime(template.last_run.created_at)}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Not run yet</span>
        )}
        <span className="font-medium text-primary">Open →</span>
      </div>
    </Link>
  );
}
